import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";

import { Tooltip } from "@lopie/kernel-ui";

/**
 * Running the app-qa agent against one custom app, from its own drawer
 * (LO-13x).
 *
 * Custom apps only — `Apps.jsx` renders this beside `Deployments` for an
 * `origin: "request"` app and not at all for a library one, the same split
 * the Custom/Library tabs already make. A run starts, is followed by
 * polling `GET /qa/{app}/{qa_run_id}` (this route holds no read open, unlike
 * a build's `?wait=`, so this component is what turns "ask again" into
 * "watch it finish"), and every finding renders as its own row with a
 * status chip — the `ReachableBy` pattern (a verdict, a name, a reason),
 * because a QA finding is exactly that shape: something is true of this app
 * and here is why.
 *
 * **Nothing here releases anything.** A `filed` finding named a build and a
 * request; the governor's Approve on Requests is a separate, later click,
 * and this panel says so rather than letting "filed" read as "done".
 */

const POLL_MS = 4000;

const STATUS_CHIP = {
  filed: { label: "filed", color: "kernel.permitted" },
  reported: { label: "reported", color: "kernel.stale" },
  not_fixed: { label: "not fixed", color: "kernel.stale" },
};

const SEVERITY_LABEL = {
  trivial: "trivial",
  notable: "notable",
};

export default function QaPanel({ appType, get, post }) {
  const [runs, setRuns] = useState(null);
  const [failure, setFailure] = useState(null);
  const [active, setActive] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const body = await get(`/qa/${appType}`);
      setRuns(body.runs || []);
      setFailure(null);
    } catch (e) {
      setFailure(e);
    }
  }, [get, appType]);

  useEffect(() => {
    load();
  }, [load]);

  const poll = useCallback(
    async (qaRunId) => {
      try {
        const body = await get(`/qa/${appType}/${qaRunId}`);
        setActive(body);
        if (body.finished) {
          clearInterval(timer.current);
          timer.current = null;
          load();
        }
      } catch (e) {
        clearInterval(timer.current);
        timer.current = null;
        setFailure(e);
      }
    },
    [get, appType, load],
  );

  useEffect(() => () => clearInterval(timer.current), []);

  const start = async (instance) => {
    setStarting(true);
    setFailure(null);
    try {
      const body = await post(`/qa/${appType}`, instance ? { instance } : {});
      setActive(body);
      timer.current = setInterval(() => poll(body.qa_run_id), POLL_MS);
    } catch (e) {
      setFailure(e);
    } finally {
      setStarting(false);
    }
  };

  const showing = active || (runs && runs[0]) || null;
  const running = Boolean(active && !active.finished);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={starting || running}
          onClick={() => start()}
        >
          {running ? "Running…" : "Run QA"}
        </Button>
        {showing ? (
          <Typography variant="caption" color="text.secondary">
            {running
              ? `Started ${showing.started_at || "just now"}`
              : `Last run ${showing.started_at || ""}`}
          </Typography>
        ) : null}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, maxWidth: "62ch" }}
      >
        Drives a headless browser against this app&apos;s live instance and
        reads its current build, under the app-qa agent&apos;s own manifest —
        never this console&apos;s. A fixable finding becomes a new build and a
        filed release request; nothing goes live until a governor separately
        approves it on Requests.
      </Typography>

      {failure ? (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>
          {failure.status === 409
            ? "Nothing to test yet — this app needs a ready, live instance first."
            : "The run could not be started or read."}
        </Alert>
      ) : null}

      {!showing && !failure ? (
        <Typography variant="body2" color="text.secondary">
          No QA run yet.
        </Typography>
      ) : null}

      {showing ? <Findings run={showing} /> : null}
    </Box>
  );
}

function Findings({ run }) {
  if (!run.finished && !run.findings?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {run.status === "IN_PROGRESS" || run.status === "QUEUED"
          ? "Running — a pass drives a browser and a model session, so this takes minutes."
          : "Waiting for a result."}
      </Typography>
    );
  }

  if (run.finished && run.status !== "SUCCEEDED") {
    return (
      <Alert severity="error" variant="outlined">
        The run did not finish — {run.status?.toLowerCase() || "unknown status"}
        {run.failure ? `: ${run.failure}` : ""}. Nothing here is a finding
        about the app itself; something stopped the run before it could
        check anything.
      </Alert>
    );
  }

  if (run.finished && !run.findings?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Finished. No findings.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {run.summary ? (
        <Typography variant="body2">{run.summary}</Typography>
      ) : null}
      <Stack spacing={0.75}>
        {(run.findings || []).map((f, i) => {
          const chip = STATUS_CHIP[f.status] || { label: f.status || "open",
            color: "kernel.stale" };
          return (
            <Stack
              key={`${f.rule}-${i}`}
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Chip
                size="small"
                variant="outlined"
                label={chip.label}
                sx={{ color: chip.color, borderColor: chip.color, minWidth: 72 }}
              />
              <Tooltip title={f.detail || ""}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, cursor: "help" }}
                >
                  {f.rule}
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {SEVERITY_LABEL[f.severity] || f.severity}
                {f.file ? ` · ${f.file}` : ""}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      {run.does_not_prove ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Does not prove: {run.does_not_prove}
        </Typography>
      ) : null}
    </Stack>
  );
}
